import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { PropType, computed, watch } from "vue";
import { Address } from "@/types/generic/Address";
import postcodeLookup from "@/components/ui/address/postcode/postcode-lookup.vue";
import { AddressLookupResult } from "@/types/address-lookup/AddressLookupResult";
import acquaintTextbox from "@/components/ui/inputs/acquaint-textbox.vue";
import countrySelect from "@/components/ui/dropdowns/country-select.vue";
import { EditAddressValidation } from "@/types/validation/EditAddressValidation";
import { FieldSize } from "@/enums/system/FieldSize";


export default /*#__PURE__*/_defineComponent({
  __name: 'edit-address',
  props: {
  /**
   * The address object containing the property details used with `v-model`.
   * This is required.
   * @type {Address}
   * @required
   */
  modelValue: {
    type: Object as PropType<Address>,
    required: true,
  },

  /**
   * Determines whether the specific control is read-only.
   * @type {boolean}
   * @default false
   */
  readOnly: {
    type: Boolean,
    default: false,
  },

  /**
   * The validation rules associated with editing the address.
   * @type {EditAddressValidation}
   * @required
   */
  validation: {
    type: Object as PropType<EditAddressValidation>,
    required: true,
  },

  /**
   * The width of the field, defined by the `FieldSize` enum.
   * @type {FieldSize}
   * @default FieldSize.Full
   */
  width: {
    type: String as PropType<FieldSize>,
    default: FieldSize.Full,
  },

  /**
   * The validation group name used for form validation.
   * @type {string}
   * @default ''
   */
  validationGroup: {
    type: String,
    default: "",
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes.
   * This is used to update the v-model variable in the parent component and control the visibility of the modal.
   * @event update:modelValue
   * @property {boolean} value - The updated state to reflect whether the modal should be shown or hidden.
   */
  "update:modelValue",
],
  setup(__props, { emit }) {

const props = __props;





const addressDetails = computed({
  get: () => props.modelValue,
  set: (value: Address) => emit("update:modelValue", value),
});

const selectAddress = (e: AddressLookupResult) => {
  addressDetails.value.propertyName = e.propertyName;
  addressDetails.value.street = e.street;
  addressDetails.value.locality = e.locality;
  addressDetails.value.town = e.town;
  addressDetails.value.region = e.region;
};

/* If, for example, a user enters in a UK address and then changes the country to, for example, Ireland,
 * the selected address is reset to avoid them saving an invalid address */
watch(
  () => addressDetails.value.countryCode,
  (value, prevValue) => {
    if (!addressDetails.value.postCode && value == prevValue) {
      return;
    }

    addressDetails.value.postCode = "";
    addressDetails.value.propertyName = "";
    addressDetails.value.street = "";
    addressDetails.value.locality = "";
    addressDetails.value.town = "";
    addressDetails.value.region = "";
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(countrySelect, {
      modelValue: addressDetails.value.countryCode,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((addressDetails.value.countryCode) = $event)),
      readOnly: props.readOnly,
      width: __props.width
    }, null, 8, ["modelValue", "readOnly", "width"]),
    _createVNode(postcodeLookup, {
      modelValue: addressDetails.value.postCode,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((addressDetails.value.postCode) = $event)),
      countryCode: addressDetails.value.countryCode,
      "field-validation": __props.validation.postcode,
      "read-only": props.readOnly,
      width: __props.width,
      onSelectAddress: selectAddress,
      validationGroup: props.validationGroup
    }, null, 8, ["modelValue", "countryCode", "field-validation", "read-only", "width", "validationGroup"]),
    _createVNode(acquaintTextbox, {
      modelValue: addressDetails.value.propertyName,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((addressDetails.value.propertyName) = $event)),
      readOnly: props.readOnly,
      fieldValidation: props.validation?.propertyName,
      width: __props.width,
      label: "Property Name",
      validationGroup: props.validationGroup
    }, null, 8, ["modelValue", "readOnly", "fieldValidation", "width", "validationGroup"]),
    _createVNode(acquaintTextbox, {
      modelValue: addressDetails.value.street,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((addressDetails.value.street) = $event)),
      readOnly: props.readOnly,
      fieldValidation: props.validation?.street,
      width: __props.width,
      label: "Street",
      validationGroup: props.validationGroup
    }, null, 8, ["modelValue", "readOnly", "fieldValidation", "width", "validationGroup"]),
    _createVNode(acquaintTextbox, {
      modelValue: addressDetails.value.locality,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((addressDetails.value.locality) = $event)),
      readOnly: props.readOnly,
      fieldValidation: props.validation?.locality,
      width: __props.width,
      label: "Locality",
      validationGroup: props.validationGroup
    }, null, 8, ["modelValue", "readOnly", "fieldValidation", "width", "validationGroup"]),
    _createVNode(acquaintTextbox, {
      modelValue: addressDetails.value.town,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((addressDetails.value.town) = $event)),
      readOnly: props.readOnly,
      fieldValidation: props.validation?.town,
      width: __props.width,
      label: "Town",
      validationGroup: props.validationGroup
    }, null, 8, ["modelValue", "readOnly", "fieldValidation", "width", "validationGroup"]),
    _createVNode(acquaintTextbox, {
      modelValue: addressDetails.value.region,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((addressDetails.value.region) = $event)),
      readOnly: props.readOnly,
      fieldValidation: props.validation?.region,
      width: __props.width,
      label: "Region",
      validationGroup: props.validationGroup
    }, null, 8, ["modelValue", "readOnly", "fieldValidation", "width", "validationGroup"])
  ], 64))
}
}

})